import React, { useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import Select from 'react-select';
import { useTask, useDeleteTask, useUpdateTask, useAllUsers, useItems, useDocuments, useCreateDocument, useSendTaskReminder, useCurrentUser } from '../hooks/useQueries';
import { useIsMobile } from '../hooks/useIsMobile';
import { useHasRole } from '../hooks/useHasRole';
import { formatDate as formatDateOnly, toDateInputValue, openDatePicker, datePickerTriggers, isValidDateInputValue, DATE_INPUT_MIN, DATE_INPUT_MAX } from '../utils/dateUtils';
import TaskComments from '../components/TaskComments';
import Toast from '../components/Popup/Toast';
import Modal from '../components/Popup/Modal';
import './itemDetailPage.css';
import { formatItemId } from '../utils/itemIdFormat';
import './taskDetailPage.css';
import '../components/Popup/StatusBadge.css';

const TaskDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isMobile = useIsMobile();
  
  // Get source for back navigation
  const source = searchParams.get('source');
  const sourceItemId = searchParams.get('sourceItemId');
  
  // Create back navigation handler
  const handleBackNavigation = React.useCallback(() => {
    if (source === 'item' && sourceItemId) {
      navigate(`/items/${sourceItemId}`);
    } else {
      navigate('/tasks');
    }
  }, [navigate, source, sourceItemId]);
  
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showReopenModal, setShowReopenModal] = useState(false);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [closingComment, setClosingComment] = useState('');
  const [reminderComment, setReminderComment] = useState('');
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [selectedAssignee, setSelectedAssignee] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [savingFields, setSavingFields] = useState(new Set());
  const [uploadModal, setUploadModal] = useState({ show: false, file: null, notes: '' });
  const [isUploadDragActive, setIsUploadDragActive] = useState(false);
  const [actionsExpanded, setActionsExpanded] = useState(false);

  // Role checks
  const { hasRole: canEdit } = useHasRole('user'); // Can edit if user or higher
  const { hasRole: isAdmin } = useHasRole('admin'); // Check if user is admin
  
  // Check if user is ONLY a viewer (has viewer role but not user role)
  const { hasRole: hasViewer } = useHasRole('viewer');
  const isViewerOnly = hasViewer && !canEdit;
  
  // Get current user
  const { data: currentUser } = useCurrentUser();


  // Helper functions for file handling
  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (mimeType) => {
    if (!mimeType) return 'bi-file-earmark';
    if (mimeType.startsWith('image/')) return 'bi-file-image';
    if (mimeType === 'application/pdf') return 'bi-file-pdf';
    return 'bi-file-earmark';
  };

  // Fetch single task
  const { data: task, isLoading, error } = useTask(id);

  // Fetch all users for assigned_to dropdown
  const { data: usersData = [] } = useAllUsers();
  
  // Map users from active endpoint format to expected format
  const users = usersData.map(user => ({
    id: user.id,
    name: user.name,
    email: user.email,
    title: user.title
  }));

  // Fetch all items for parent_id dropdown
  const { data: allItems = [] } = useItems();

  // Document functionality for parent item
  const parentItemId = task?.parent_id;
  const { data: docData = {} } = useDocuments(parentItemId, { enabled: !!parentItemId });
  const documents = docData.documents || [];
  const createDocumentMutation = useCreateDocument();

  // Delete task mutation
  const deleteTaskMutation = useDeleteTask({
    onSuccess: () => {
      showToast('Task moved to trash', 'success');
      handleBackNavigation();
    },
    onError: (error) => {
      showToast('Failed to delete task', 'error');
      console.error('Delete error:', error);
    }
  });

  // Update task mutation
  const updateTaskMutation = useUpdateTask({
    onMutate: ({ fieldId }) => {
      setSavingFields(prev => new Set([...prev, fieldId]));
    },
    onSuccess: (data, { fieldId }) => {
      setSavingFields(prev => {
        const newSet = new Set(prev);
        newSet.delete(fieldId);
        return newSet;
      });
      setEditingField(null);
      if (fieldId === 'status' && data.status === 'Completed') {
        showToast('Task marked as completed successfully!', 'success');
      } else {
        showToast('Task updated successfully!', 'success');
      }
    },
    onError: (error, { fieldId }) => {
      setSavingFields(prev => {
        const newSet = new Set(prev);
        newSet.delete(fieldId);
        return newSet;
      });
      // A refused transition is a rule the user needs to read, not a failure
      // they need to debug, so it is shown as the server worded it.
      showToast(
        error.details?.code === 'TASK_NOT_STARTED'
          ? error.message
          : `Failed to update task: ${error.message}`,
        'error'
      );
    }
  });

  // Send reminder email mutation
  const sendReminderMutation = useSendTaskReminder({
    onSuccess: (data) => {
      setShowReminderModal(false);
      setReminderComment('');
      if (data?.skipped) {
        showToast(data?.message || 'Reminder email skipped due to notification preferences', 'info');
      } else {
        showToast(data?.message || 'Reminder email sent successfully!', 'success');
      }
    },
    onError: (error) => {
      showToast(`Failed to send reminder: ${error.message}`, 'error');
    }
  });

  // Helper function to show toast
  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };
  
  // Upload functionality
  const handleUploadClick = () => {
    setUploadModal({ show: true, file: null, notes: '' });
  };

  const handleSelectedFile = (file) => {
    if (!file) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      showToast('Only PDF and image files are allowed', 'error');
      return;
    }

    // Check file size (50MB max)
    const maxSize = 50 * 1024 * 1024; // 50MB in bytes
    if (file.size > maxSize) {
      showToast('File size must be less than 50MB', 'error');
      return;
    }

    setUploadModal(prev => ({ ...prev, file }));
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    handleSelectedFile(file);

    // Allow selecting the same file again
    event.target.value = '';
  };

  const handleUploadDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsUploadDragActive(true);
  };

  const handleUploadDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsUploadDragActive(true);
  };

  const handleUploadDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsUploadDragActive(false);
  };

  const handleUploadDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsUploadDragActive(false);

    const file = e.dataTransfer?.files?.[0];
    handleSelectedFile(file);
  };

  const handleConfirmUpload = async () => {
    if (!uploadModal.file || !task?.parent_id) return;

    try {
      await createDocumentMutation.mutateAsync({
        itemId: task.parent_id,
        file: uploadModal.file,
        notes: uploadModal.notes
      });
      showToast('Document uploaded successfully to parent item');
      setUploadModal({ show: false, file: null, notes: '' });
    } catch (error) {
      showToast(error.message || 'Failed to upload document', 'error');
    }
  };

  // Helper function to handle field save
  const handleFieldSave = (fieldId, value) => {
    console.log('Saving field:', fieldId, 'with value:', value);
    
    // Prevent duplicate mutations if this field is already being saved
    if (savingFields.has(fieldId)) {
      console.log('Skipping duplicate mutation for field:', fieldId);
      return;
    }

    updateTaskMutation.mutate({ 
      id: parseInt(id),
      [fieldId]: value,
      fieldId // Pass fieldId for the callbacks
    });
  };

  // Helper function to handle field edit
  const handleFieldEdit = (fieldId, value) => {
    handleFieldSave(fieldId, value);
  };

  // Helper function to handle delete
  const handleDelete = () => {
    deleteTaskMutation.mutate(parseInt(id));
  };

  // Helper function to handle mark completed
  const handleMarkCompleted = () => {
    updateTaskMutation.mutate({ 
      id: parseInt(id),
      status: 'Completed',
      closing_comment: closingComment || null,
      fieldId: 'status'
    });
    setShowCompleteModal(false);
    setClosingComment('');
  };

  // Helper function to start a task
  //
  // No confirmation modal and no closing comment: starting work is not
  // destructive and is undone by simply finishing or canceling the task.
  const handleStartTask = () => {
    updateTaskMutation.mutate({
      id: parseInt(id),
      status: 'In Progress',
      fieldId: 'status'
    });
  };

  // Helper function to handle mark canceled
  const handleMarkCanceled = () => {
    setShowCancelModal(true);
  };
  
  // Helper function to confirm cancel with comment
  const handleConfirmCancel = () => {
    updateTaskMutation.mutate({ 
      id: parseInt(id),
      status: 'Canceled',
      closing_comment: closingComment || null,
      fieldId: 'status'
    });
    setShowCancelModal(false);
    setClosingComment('');
  };

  // Helper function to reopen a completed task
  const handleReopenTask = () => {
    setShowReopenModal(true);
  };

  const handleConfirmReopenTask = async () => {
    if (task?.parent_item_status === 'closed') {
      showToast('Cannot reopen a task whose parent item is closed.', 'error');
      setShowReopenModal(false);
      return;
    }

    updateTaskMutation.mutate({
      id: parseInt(id),
      status: 'Assigned',
      fieldId: 'status'
    });
    setShowReopenModal(false);
  };

  // Helper function to handle reassign
  const handleReassign = () => {
    setShowReassignModal(true);
    // Don't pre-select current assignee since they're excluded from options
    setSelectedAssignee(null);
  };

  const handleConfirmReassign = () => {
    if (!selectedAssignee) {
      showToast('Please select a user to assign the task to', 'error');
      return;
    }
    
    // Check if trying to reassign to the same user
    const currentAssignee = users.find(user => user.name === task?.assigned_to || user.email === task?.assigned_to);
    if (currentAssignee && selectedAssignee.id === currentAssignee.id) {
      showToast('Task is already assigned to this user', 'error');
      return;
    }
    
    updateTaskMutation.mutate({ 
      id: parseInt(id),
      assigned_to: selectedAssignee.name || selectedAssignee.email,
      fieldId: 'assigned_to'
    });
    setShowReassignModal(false);
    setSelectedAssignee(null);
  };

  // Get current assignee for reassign logic
  const getCurrentAssignee = () => {
    return users.find(user => user.name === task?.assigned_to || user.email === task?.assigned_to);
  };
  
  // Check if current user can reassign this task
  const canReassignTask = () => {
    if (!task || !currentUser) return false;
    // User can reassign if they are admin OR if the task is assigned to them
    if (isAdmin) return true;
    // Check if task is assigned to current user (by name or email)
    return task.assigned_to === currentUser.name || task.assigned_to === currentUser.email;
  };

  // Check if selected user is different from current assignee
  const isReassignValid = () => {
    if (!selectedAssignee) return false;
    const currentAssignee = getCurrentAssignee();
    return !currentAssignee || selectedAssignee.id !== currentAssignee.id;
  };

  // Handle sending reminder email
  const handleSendReminder = () => {
    setShowReminderModal(true);
  };

  const handleConfirmSendReminder = () => {
    sendReminderMutation.mutate({
      id: parseInt(id),
      comment: reminderComment.trim()
    });
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format due date (date-only, timezone-safe so it matches the edit input)
  const formatDueDate = (dateString) => {
    if (!dateString) return 'Not set';
    const formatted = formatDateOnly(dateString);
    return formatted === '-' ? 'Not set' : formatted;
  };

  // Render inline edit field
  const renderInlineEditField = (fieldId, fieldType, fieldValue) => {
    const isSaving = savingFields.has(fieldId);
    
    const handleBlur = (e) => {
      const newValue = e.target.value;
      if (newValue !== fieldValue) {
        handleFieldEdit(fieldId, newValue);
      } else {
        setEditingField(null);
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Enter' && fieldType !== 'textarea') {
        e.target.blur();
      } else if (e.key === 'Escape') {
        setEditingField(null);
      }
    };

    if (fieldType === 'select' && fieldId === 'status') {
      const statusOptions = [
        { value: 'Assigned', label: 'Assigned' },
        { value: 'In Progress', label: 'In Progress' },
        { value: 'Completed', label: 'Completed' },
        { value: 'Canceled', label: 'Canceled' }
      ];

      const currentValue = fieldValue ? statusOptions.find(opt => opt.value === fieldValue) : null;

      return (
        <Select
          className="inline-edit-select"
          value={currentValue}
          onChange={(selectedOption) => {
            const newValue = selectedOption ? selectedOption.value : '';
            if (newValue !== fieldValue) {
              handleFieldEdit(fieldId, newValue);
            } else {
              setEditingField(null);
            }
          }}
          onBlur={() => setEditingField(null)}
          options={statusOptions}
          isClearable
          isDisabled={isSaving}
          autoFocus
          placeholder="Select status..."
        />
      );
    } else if (fieldType === 'select' && fieldId === 'assigned_to') {
      const userOptions = users.map(user => ({
        value: user.name,
        label: user.name
      }));

      const currentValue = fieldValue ? { value: fieldValue, label: fieldValue } : null;

      return (
        <Select
          className="inline-edit-select"
          value={currentValue}
          onChange={(selectedOption) => {
            const newValue = selectedOption ? selectedOption.value : null;
            if (newValue !== fieldValue) {
              handleFieldEdit(fieldId, newValue);
            } else {
              setEditingField(null);
            }
          }}
          onBlur={() => setEditingField(null)}
          options={userOptions}
          isClearable
          isDisabled={isSaving}
          autoFocus
          placeholder="Select user..."
        />
      );
    } else if (fieldType === 'select' && fieldId === 'parent_id') {
      const itemOptions = allItems.map(item => ({
        value: item.id,
        label: `(ID: ${formatItemId(item.id)}) ${item.title || 'No title'}`
      }));

      const currentValue = fieldValue ? { 
        value: fieldValue, 
        label: itemOptions.find(opt => opt.value === fieldValue)?.label || `(ID: ${formatItemId(fieldValue)}) Unknown Item`
      } : null;

      return (
        <Select
          className="inline-edit-select"
          value={currentValue}
          onChange={(selectedOption) => {
            const newValue = selectedOption ? selectedOption.value : null;
            if (newValue !== fieldValue) {
              handleFieldEdit(fieldId, newValue);
            } else {
              setEditingField(null);
            }
          }}
          onBlur={() => setEditingField(null)}
          options={itemOptions}
          isClearable
          isDisabled={isSaving}
          autoFocus
          placeholder="Select parent item..."
        />
      );
    } else if (fieldType === 'textarea') {
      return (
        <textarea
          className="inline-edit-field"
          defaultValue={fieldValue || ''}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          disabled={isSaving}
          autoFocus
          rows={3}
        />
      );
    } else if (fieldType === 'date') {
      // Uncontrolled and saved on blur/Enter only. While a year is being typed the
      // input reports intermediate values ("0002-…", "0020-…", "0202-…"), so saving
      // on every change persisted a bogus year and re-rendered over the user's typing.
      const handleDateBlur = (e) => {
        const newValue = e.target.value || null;
        const currentDateValue = fieldValue ? toDateInputValue(fieldValue) : null;
        if (newValue === currentDateValue) {
          setEditingField(null);
          return;
        }
        // badInput: a partly entered date, which the input reports as "" - don't
        // let that clear the due date.
        if (e.target.validity.badInput || (newValue && !isValidDateInputValue(newValue))) {
          showToast('Please enter a valid date between 1900 and 9999', 'error');
          setEditingField(null);
          return;
        }
        handleFieldEdit(fieldId, newValue);
      };

      return (
        <>
          <input
            type="date"
            className="inline-edit-field"
            defaultValue={fieldValue ? toDateInputValue(fieldValue) : ''}
            min={DATE_INPUT_MIN}
            max={DATE_INPUT_MAX}
            // Mounted by the click on the displayed date, so open the calendar on
            // that first focus; later clicks follow datePickerTriggers.
            onFocus={openDatePicker}
            {...datePickerTriggers}
            onBlur={handleDateBlur}
            onKeyDown={handleKeyDown}
            disabled={isSaving}
            autoFocus
          />
          {fieldValue && (
            <button
              type="button"
              className="inline-edit-clear"
              // Keep focus on the input so its blur doesn't save the old date first.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleFieldEdit(fieldId, null)}
              disabled={isSaving}
            >
              Clear
            </button>
          )}
        </>
      );
    } else {
      return (
        <input
          type="text"
          className="inline-edit-field"
          value={fieldValue || ''}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          disabled={isSaving}
          autoFocus
        />
      );
    }
  };

  // Render field value
  const renderFieldValue = (fieldId, label, value, type = 'text') => {
    const isCurrentlyEditing = editingField === fieldId;
    const isSaving = savingFields.has(fieldId);
    const isEditable = canEdit && !isViewerOnly &&
      fieldId !== 'created_on' &&
      fieldId !== 'completed_on' &&
      fieldId !== 'created_by' &&
      fieldId !== 'assigned_to' &&
      fieldId !== 'closing_comment' &&
      fieldId !== 'parent_id'; // Make created_on, completed_on, created_by, assigned_to, closing_comment, and parent_id read-only

    return (
      <div className="detail-field">
        <label>{label}</label>
        <div className={`field-value-container ${isEditable ? 'editable' : ''} ${isCurrentlyEditing ? 'editing' : ''}`}>
          {isCurrentlyEditing && isEditable ? (
            renderInlineEditField(fieldId, type, value)
          ) : (
            <div 
              className="field-value"
              onClick={() => isEditable && setEditingField(fieldId)}
            >
              {type === 'status' || (type === 'select' && fieldId === 'status') ? (
                <span className={`status-badge status-${value?.replace('_', '-')?.toLowerCase() || 'unknown'}`}>
                  {value ? value.replace('_', ' ') : 'Not set'}
                </span>
              ) : type === 'date' ? (
                value ? formatDueDate(value) : 'Not set'
              ) : type === 'datetime' ? (
                value ? formatDate(value) : 'Not set'
              ) : (fieldId === 'parent_id' && value) ? (
                <a 
                  href={`/items/${value}?source=task&sourceTaskId=${task.id}`} 
                  className="item-id-pill"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/items/${value}?source=task&sourceTaskId=${task.id}`);
                  }}
                >
                  (ID: {formatItemId(value)}) {task.parent_title || ''}
                </a>
              ) : type === 'link' && value ? (
                <a 
                  href={`/items/${value}`} 
                  className="item-id-pill"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/items/${value}`);
                  }}
                >
                  (ID: {formatItemId(value)}) {task.parent_title || ''}
                </a>
              ) : (type === 'select' && fieldId === 'parent_id' && value) ? (
                (() => {
                  const parentItem = allItems.find(item => item.id === value);
                  return parentItem ?
                    `(ID: ${formatItemId(value)}) ${parentItem.title || 'No title'}` :
                    `(ID: ${formatItemId(value)}) Unknown Item`;
                })()
              ) : (
                value || (isEditable ? 'Click to edit...' : 'Not set')
              )}
            </div>
          )}
          {isSaving && (
            <div className="field-spinner">
              <i className="bi bi-arrow-clockwise spin"></i>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="item-detail-page task-detail-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading task details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="item-detail-page task-detail-page">
        <div className="error-container">
          <h2>Error Loading Task</h2>
          <p>{error.message}</p>
          <button 
            className="btn btn-primary" 
            onClick={handleBackNavigation}
          >
            {source === 'item' ? 'Back to Item' : 'Back to Tasks'}
          </button>
        </div>
      </div>
    );
  }

  if (!task) {
    return (
      <div className="item-detail-page task-detail-page">
        <div className="error-container">
          <h2>Task Not Found</h2>
          <p>The task you're looking for doesn't exist or you don't have permission to view it.</p>
          <button 
            className="btn btn-primary" 
            onClick={handleBackNavigation}
          >
            {source === 'item' ? 'Back to Item' : 'Back to Tasks'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`item-detail-page task-detail-page ${isMobile ? 'task-detail-page--mobile' : ''}`}>
      <div className="item-detail-container">
        {/* Header */}
        <div className="detail-header">
          <button
            className="back-btn task-detail-back"
            onClick={handleBackNavigation}
            title={source === 'item' ? 'Back to Item' : 'Back to Tasks'}
          >
            <i className="bi bi-arrow-left"></i>
          </button>

          <div className="task-detail-title">
            <h1>Task #{task.id}</h1>
          </div>

          <div className="task-detail-status">
            <span className="task-detail-status-label">Task Status:</span>
            <span className={`status-badge status-${task.status?.replace('_', '-')?.toLowerCase() || 'unknown'}`}>
              {task.status?.replace('_', ' ') || 'Unknown'}
            </span>
          </div>
        </div>

        {/* Actions Section */}
        {!isViewerOnly && (
          <div className="detail-section actions-section">
            {isMobile ? (
              <button
                type="button"
                className="actions-toggle"
                onClick={() => setActionsExpanded((prev) => !prev)}
                aria-expanded={actionsExpanded}
                aria-controls="task-actions-panel"
              >
                <span className="actions-toggle__title">Actions</span>
                <i
                  className={`bi ${actionsExpanded ? 'bi-chevron-up' : 'bi-chevron-down'}`}
                  aria-hidden="true"
                />
              </button>
            ) : (
              <div className="actions-toggle" role="heading" aria-level={2}>
                <span className="actions-toggle__title">Actions</span>
              </div>
            )}

            {(isMobile ? actionsExpanded : true) && (
              <div id="task-actions-panel" className="actions-grid">
                {/* A task is started before it is finished: work that was never
                    picked up cannot be reported as done. Assigned -> In Progress
                    -> Completed, with the server enforcing the same order. */}
                {task.status === 'Assigned' && (
                  <button
                    className="btn"
                    onClick={handleStartTask}
                    disabled={updateTaskMutation.isPending}
                    title="Start Work on This Task"
                    style={{
                      // The colour the In Progress badge already uses, so the
                      // button and the state it produces read as the same thing.
                      backgroundColor: '#0c5460',
                      color: 'white',
                      border: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      padding: '12px 20px'
                    }}
                  >
                    <i className="bi bi-play-circle-fill"></i>
                    Start Task
                  </button>
                )}
                {(task.status === 'Assigned' || task.status === 'In Progress') && (() => {
                  const isStarted = task.status === 'In Progress';

                  const completeButton = (
                    <button
                      className="btn"
                      onClick={() => setShowCompleteModal(true)}
                      disabled={updateTaskMutation.isPending || !isStarted}
                      title={isStarted
                        ? 'Mark Task as Completed'
                        : 'Start the task before completing it'}
                      style={{
                        backgroundColor: 'var(--nsi-brand)',
                        color: 'white',
                        border: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        padding: '12px 20px',
                        // Dimmed as well as disabled: a full-strength brand
                        // button that does nothing reads as broken.
                        ...(isStarted ? {} : { pointerEvents: 'none', width: '100%', opacity: 0.55 })
                      }}
                    >
                      <i className="bi bi-check-circle-fill"></i>
                      Mark Completed
                    </button>
                  );

                  if (isStarted) return completeButton;

                  // Shown rather than hidden, so the next step is visible from
                  // the start and the tooltip says what unlocks it. Same
                  // disabled-with-tooltip pattern as Reopen below.
                  return (
                    <span className="tooltip-container" style={{ width: '100%' }}>
                      {completeButton}
                      <div className="tooltip-content">
                        Mark the task In Progress first - use Start Task
                      </div>
                    </span>
                  );
                })()}
                {task.status === 'Completed' ? (
                  (() => {
                    const isParentClosed = task.parent_item_status === 'closed';

                    const reopenButton = (
                      <button
                        className="btn btn-primary"
                        onClick={handleReopenTask}
                        disabled={updateTaskMutation.isPending || isParentClosed}
                        style={{ 
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                          padding: '12px 20px',
                          width: '100%'
                        }}
                      >
                        <i className="bi bi-arrow-counterclockwise"></i>
                        Reopen
                      </button>
                    );

                    if (!isParentClosed) {
                      return reopenButton;
                    }

                    return (
                      <span className="tooltip-container" style={{ width: '100%' }}>
                        {/* Match ItemDetailPage tooltip styling (App.css tooltip-container/tooltip-content). */}
                        <button
                          className="btn btn-primary"
                          onClick={handleReopenTask}
                          disabled={true}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            padding: '12px 20px',
                            pointerEvents: 'none',
                            width: '100%'
                          }}
                        >
                          <i className="bi bi-arrow-counterclockwise"></i>
                          Reopen
                        </button>
                        <div className="tooltip-content">
                          Cannot reopen tasks under a closed parent item
                        </div>
                      </span>
                    );
                  })()
                ) : task.status !== 'Canceled' && (
                  <button
                    className="btn btn-warning"
                    onClick={handleMarkCanceled}
                    disabled={updateTaskMutation.isPending}
                    title="Mark Task as Canceled"
                    style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '12px 20px'
                    }}
                  >
                    <i className="bi bi-x-circle-fill"></i>
                    Mark Canceled
                  </button>
                )}
                {task.status !== 'Completed' && task.status !== 'Canceled' && canReassignTask() && (
                  <button
                    className="btn btn-info"
                    onClick={handleReassign}
                    disabled={updateTaskMutation.isPending}
                    title="Reassign Task"
                    style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '12px 20px'
                    }}
                  >
                    <i className="bi bi-person-plus-fill"></i>
                    Reassign
                  </button>
                )}
                {task.status !== 'Completed' && task.status !== 'Canceled' && (
                  <button
                    className="btn btn-secondary"
                    onClick={handleSendReminder}
                    disabled={sendReminderMutation.isPending || !task.assigned_to}
                    title="Send Reminder Email to Assigned User"
                    style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '12px 20px'
                    }}
                  >
                    <i className="bi bi-envelope-fill"></i>
                    Send Reminder
                  </button>
                )}
                <button
                  className="btn btn-danger"
                  onClick={() => setShowDeleteModal(true)}
                  disabled={deleteTaskMutation.isPending}
                  title="Delete Task"
                  style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    padding: '12px 20px',
                    width: '100%'
                  }}
                >
                  <i className="bi bi-trash3-fill"></i>
                  Delete Task
                </button>
              </div>
            )}
          </div>
        )}

        {/* Content */}
        <div className="detail-content">
          <div className="detail-section">
            <h4>Task Information</h4>
            <div className="detail-grid two-column">
              {renderFieldValue('description', 'Description', task.description, 'textarea')}
              {renderFieldValue('parent_id', 'Parent Item', task.parent_id, 'link')}
              {renderFieldValue('due_date', 'Due Date', task.due_date, 'date')}
              {renderFieldValue('assigned_to', 'Assigned To', task.assigned_to, 'select')}
              {renderFieldValue('created_by', 'Created By', task.created_by, 'text')}
              {renderFieldValue('created_on', 'Created On', task.created_on, 'datetime')}
              {task.completed_on &&
                renderFieldValue('completed_on', 'Completed On', task.completed_on, 'datetime')
              }
              {(task.status === 'Completed' || task.status === 'Canceled') && task.closing_comment &&
                renderFieldValue('closing_comment', 'Closing Comment', task.closing_comment, 'textarea')
              }
            </div>
          </div>
          
          {/* Documents Section - Read-only view of parent item documents */}
          {task.parent_id && (
            <div className="detail-section">
              <div className="task-documents-header">
                <h4>Related Documents ({documents.length})</h4>
                <div className="task-documents-actions">
                  {!isViewerOnly && (
                    <button
                      className="btn btn-primary"
                      onClick={handleUploadClick}
                      disabled={createDocumentMutation.isPending}
                      title="Upload Document to Parent Item"
                      style={{ 
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontSize: '14px'
                      }}
                    >
                      <i className="bi bi-cloud-upload"></i>
                      Upload Document
                    </button>
                  )}
                  <button
                    className="btn btn-link"
                    onClick={() => navigate(`/items/${task.parent_id}?source=task&sourceTaskId=${task.id}#documents`)}
                    title="View all documents in parent item"
                    style={{ fontSize: '14px', textDecoration: 'none' }}
                  >
                    <i className="bi bi-box-arrow-up-right me-1"></i>
                    View in Parent Item
                  </button>
                </div>
              </div>
              
              {documents.length > 0 ? (
                <div className="documents-preview">
                  <div className="documents-list">
                    {documents.slice(0, 5).map(doc => (
                      <div key={doc.id} className="document-item">
                        <div className="document-info">
                          <i className={`${getFileIcon(doc.mime_type)} me-2`} style={{ 
                            color: doc.mime_type === 'application/pdf' ? '#dc3545' : 
                                   doc.mime_type.startsWith('image/') ? '#10b981' : '#6c757d' 
                          }}></i>
                          <span className="document-name" title={doc.filename}>
                            {doc.filename.length > 40 ? `${doc.filename.substring(0, 40)}...` : doc.filename}
                          </span>
                        </div>
                        <div className="document-meta">
                          <span className="document-size">{formatFileSize(doc.file_size)}</span>
                          <span className="document-date">{new Date(doc.uploaded_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    ))}
                    {documents.length > 5 && (
                      <div className="document-item more-items">
                        <span>+{documents.length - 5} more documents</span>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="no-documents">
                  <i className="bi bi-file-earmark text-muted me-2"></i>
                  <span className="text-muted">No documents attached to parent item</span>
                </div>
              )}
            </div>
          )}
          
          {/* Comments Section */}
          <div className="detail-section">
            <TaskComments taskId={task.id} isViewer={isViewerOnly} />
          </div>
        </div>

        <Modal
          show={showDeleteModal}
          title="Confirm Delete"
          onCancel={() => setShowDeleteModal(false)}
          onConfirm={handleDelete}
          confirmText="Delete"
          cancelText="Cancel"
          confirmBtnClass="btn-danger"
          loading={deleteTaskMutation.isPending}
          content={
            <div className="delete-modal-content">
              <div className="alert alert-warning">
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                <strong>Note:</strong> This task will be moved to the trashcan where it can be recovered for 30 days.
              </div>
              <p className="mb-0">
                Are you sure you want to delete task <strong>#{id}</strong>?
              </p>
              {task?.description && (
                <p className="text-muted mb-0">
                  <strong>Description:</strong> {task.description.length > 100 ? 
                    `${task.description.substring(0, 100)}...` : 
                    task.description
                  }
                </p>
              )}
            </div>
          }
        />

        <Modal
          show={showCompleteModal}
          title="Mark Task as Completed"
          onCancel={() => {
            setShowCompleteModal(false);
            setClosingComment('');
          }}
          onConfirm={handleMarkCompleted}
          confirmText="Mark Completed"
          cancelText="Cancel"
          confirmBtnClass="btn-nsi-green"
          loading={updateTaskMutation.isPending}
          content={
            <div className="complete-modal-content">
              <div className="alert alert-info">
                <i className="bi bi-info-circle-fill me-2"></i>
                <strong>Complete Task:</strong> This will mark the task as completed.
              </div>
              <p className="mb-3">
                You are about to mark task <strong>#{id}</strong> as completed.
              </p>
              {task?.description && (
                <p className="text-muted mb-3">
                  <strong>Description:</strong> {task.description.length > 100 ? 
                    `${task.description.substring(0, 100)}...` : 
                    task.description
                  }
                </p>
              )}
              <div className="form-group">
                <label htmlFor="closing-comment" className="form-label">
                  <strong>Closing Comment</strong> <span className="text-muted">(optional)</span>
                </label>
                <textarea
                  id="closing-comment"
                  className="form-control"
                  style={{ width: '100%', resize: 'vertical' }}
                  rows={3}
                  value={closingComment}
                  onChange={(e) => setClosingComment(e.target.value)}
                  placeholder="Add any notes about the task completion..."
                />
              </div>
            </div>
          }
        />
        
        {/* Cancel Modal */}
        <Modal
          show={showCancelModal}
          title="Mark Task as Canceled"
          onCancel={() => {
            setShowCancelModal(false);
            setClosingComment('');
          }}
          onConfirm={handleConfirmCancel}
          confirmText="Mark Canceled"
          cancelText="Cancel"
          confirmBtnClass="btn-warning"
          loading={updateTaskMutation.isPending}
          content={
            <div className="cancel-modal-content">
              <div className="alert alert-warning">
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                <strong>Cancel Task:</strong> This will mark the task as canceled.
              </div>
              <p className="mb-3">
                You are about to mark task <strong>#{id}</strong> as canceled.
              </p>
              {task?.description && (
                <p className="text-muted mb-3">
                  <strong>Description:</strong> {task.description.length > 100 ? 
                    `${task.description.substring(0, 100)}...` : 
                    task.description
                  }
                </p>
              )}
              <div className="form-group">
                <label htmlFor="cancel-comment" className="form-label">
                  <strong>Closing Comment</strong> <span className="text-muted">(optional)</span>
                </label>
                <textarea
                  id="cancel-comment"
                  className="form-control"
                  style={{ width: '100%', resize: 'vertical' }}
                  rows={3}
                  value={closingComment}
                  onChange={(e) => setClosingComment(e.target.value)}
                  placeholder="Add any notes about the task cancellation..."
                />
              </div>
            </div>
          }
        />

        {/* Reopen Modal */}
        <Modal
          show={showReopenModal}
          title="Reopen Task"
          onCancel={() => setShowReopenModal(false)}
          onConfirm={handleConfirmReopenTask}
          confirmText="Reopen"
          cancelText="Cancel"
          confirmBtnClass="btn-primary"
          loading={updateTaskMutation.isPending}
          content={
            <div>
              <div className="alert alert-warning">
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                <strong>Are you sure?</strong>
                <div style={{ marginTop: '8px' }}>
                  Reopening will email the assigned person and will block the parent item from closing until the task is completed or canceled.
                </div>
              </div>
              <p className="mb-0">
                You are about to reopen task <strong>#{id}</strong>.
              </p>
              {task?.parent_item_status === 'closed' && (
                <div className="alert alert-danger" style={{ marginTop: '12px' }}>
                  <i className="bi bi-lock-fill me-2"></i>
                  This task cannot be reopened because its parent item is closed.
                </div>
              )}
            </div>
          }
        />

        {/* Reminder Modal */}
        <Modal
          show={showReminderModal}
          title="Send Reminder"
          onCancel={() => {
            setShowReminderModal(false);
            setReminderComment('');
          }}
          onConfirm={handleConfirmSendReminder}
          confirmText="Send Reminder"
          cancelText="Cancel"
          confirmBtnClass="btn-secondary"
          confirmDisabled={!task?.assigned_to}
          loading={sendReminderMutation.isPending}
          content={
            <div>
              <div className="alert alert-warning">
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                <strong>Are you sure?</strong>
                <div style={{ marginTop: '8px' }}>
                  This will send a reminder email to the assigned user.
                </div>
              </div>

              <p className="mb-3">
                You are about to send a reminder for task <strong>#{id}</strong>.
              </p>

              {task?.assigned_to && (
                <p className="text-muted mb-3">
                  <strong>Assigned To:</strong> {task.assigned_to}
                </p>
              )}

              <div className="form-group">
                <label htmlFor="reminder-comment" className="form-label">
                  <strong>Comment</strong> <span className="text-muted">(optional)</span>
                </label>
                <textarea
                  id="reminder-comment"
                  className="form-control"
                  style={{ width: '100%', resize: 'vertical' }}
                  rows={3}
                  value={reminderComment}
                  onChange={(e) => setReminderComment(e.target.value)}
                  placeholder="Add an optional note to include in the reminder email..."
                />
              </div>
            </div>
          }
        />
        
        {/* Reassign Modal */}
        <Modal
          show={showReassignModal}
          title="Reassign Task"
          onCancel={() => {
            setShowReassignModal(false);
            setSelectedAssignee(null);
          }}
          onConfirm={handleConfirmReassign}
          confirmText="Reassign Task"
          cancelText="Cancel"
          loading={updateTaskMutation.isPending}
          confirmDisabled={!isReassignValid()}
          content={
            <div>
              {(() => {
                const currentAssignee = getCurrentAssignee();
                return currentAssignee ? (
                  <div className="alert alert-info" style={{ marginBottom: '16px' }}>
                    <i className="bi bi-person-fill me-2"></i>
                    Currently assigned to: <strong>{currentAssignee.name}</strong>
                  </div>
                ) : (
                  <div className="alert alert-warning" style={{ marginBottom: '16px' }}>
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    This task is currently unassigned
                  </div>
                );
              })()} 
              <p>Select a user to assign this task to:</p>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Assign to User:
                </label>
                <Select
                  value={selectedAssignee ? { 
                    value: selectedAssignee.id, 
                    label: `${selectedAssignee.name} (${selectedAssignee.email})` 
                  } : null}
                  onChange={(selectedOption) => {
                    const user = users.find(u => u.id === selectedOption?.value);
                    setSelectedAssignee(user || null);
                  }}
                  options={users
                    .filter(user => {
                      // Exclude current assignee from options
                      const currentAssignee = getCurrentAssignee();
                      return !currentAssignee || user.id !== currentAssignee.id;
                    })
                    .map(user => ({
                      value: user.id,
                      label: `${user.name} (${user.email})`
                    }))
                  }
                  isClearable
                  placeholder="Select a user..."
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: base => ({ ...base, zIndex: 9999 })
                  }}
                />
              </div>
              {selectedAssignee && (
                <div className="alert alert-info">
                  <i className="bi bi-info-circle me-2"></i>
                  Task will be reassigned to: <strong>{selectedAssignee.name}</strong>
                </div>
              )}
            </div>
          }
        />
        
        {/* Upload Modal */}
        {uploadModal.show && createPortal(
          <Modal
            title="Upload Document to Parent Item"
            show={uploadModal.show}
            onCancel={() => setUploadModal({ show: false, file: null, notes: '' })}
            onConfirm={handleConfirmUpload}
            confirmText="Upload"
            cancelText="Cancel"
            confirmDisabled={!uploadModal.file || createDocumentMutation.isPending}
            content={
              <div className="upload-container">
                <div className="upload-info" style={{ 
                  padding: '12px', 
                  backgroundColor: '#e3f2fd', 
                  border: '1px solid #bbdefb', 
                  borderRadius: '6px',
                  marginBottom: '16px',
                  fontSize: '14px',
                  color: '#1565c0'
                }}>
                  <i className="bi bi-info-circle me-2"></i>
                  This document will be attached to the parent item (ID: {formatItemId(task?.parent_id)})
                </div>
                
                <div 
                  className={`upload-area ${uploadModal.file ? 'has-file' : ''} ${isUploadDragActive ? 'drag-active' : ''}`}
                  onClick={() => document.getElementById('task-file-input').click()}
                  onDragEnter={handleUploadDragEnter}
                  onDragOver={handleUploadDragOver}
                  onDragLeave={handleUploadDragLeave}
                  onDrop={handleUploadDrop}
                  style={{
                    border: '2px dashed #cbd5e1',
                    borderRadius: '8px',
                    padding: '32px',
                    textAlign: 'center',
                    background: (uploadModal.file || isUploadDragActive) ? '#f0fdf4' : '#f8fafc',
                    borderColor: (uploadModal.file || isUploadDragActive) ? '#10b981' : '#cbd5e1',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    marginBottom: '20px'
                  }}
                >
                  <input
                    id="task-file-input"
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
                    style={{ display: 'none' }}
                    onChange={handleFileSelect}
                  />
                  {!uploadModal.file ? (
                    <>
                      <div style={{ fontSize: '48px', color: '#94a3b8', marginBottom: '16px' }}>
                        <i className="bi bi-cloud-upload"></i>
                      </div>
                      <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '8px' }}>
                        Click or drag & drop a file
                      </div>
                      <div style={{ fontSize: '12px', color: '#94a3b8' }}>
                        PDF, JPG, PNG, GIF, WEBP (max 50MB)
                      </div>
                    </>
                  ) : (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      padding: '12px',
                      background: 'white',
                      borderRadius: '6px',
                      marginTop: '16px'
                    }}>
                      <i className={`${getFileIcon(uploadModal.file.type)}`} style={{ fontSize: '24px' }}></i>
                      <div style={{ textAlign: 'left' }}>
                        <div style={{ fontWeight: '500', color: '#1e293b' }}>{uploadModal.file.name}</div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>{formatFileSize(uploadModal.file.size)}</div>
                      </div>
                    </div>
                  )}
                </div>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <label style={{ fontWeight: '500', marginBottom: '8px' }}>Notes (optional)</label>
                  <textarea
                    placeholder="Add notes about this document..."
                    value={uploadModal.notes}
                    onChange={(e) => setUploadModal(prev => ({ ...prev, notes: e.target.value }))}
                    style={{
                      width: '100%',
                      minHeight: '120px',
                      padding: '12px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '14px',
                      resize: 'vertical',
                      fontFamily: 'inherit'
                    }}
                  />
                </div>
              </div>
            }
          />,
          document.body
        )}

        {/* Toast */}
        {toast.show && createPortal(
          <Toast
            show={toast.show}
            message={toast.message}
            type={toast.type}
            onClose={() => setToast({ show: false, message: '', type: 'success' })}
          />,
          document.body
        )}
      </div>
    </div>
  );
};

export default TaskDetailPage;